import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {Button} from 'UI/components/action/Button';
import {H1} from 'UI/components/layout/Text';

import {useHeroContent} from './useHeroContent';

import type {HeroBlockType} from 'types/payload-types';

/**
 * The props for the `HeroContent` component, which renders a hero section headline and optional CTA buttons.
 * This interface defines the structure required to configure the hero's text, button display, theme, and
 * accessibility behavior. It includes options for presentational rendering and automated testing identifiers.
 */
interface ComponentProps {
    /**
     * The configuration for the hero section's buttons, including definitions for the primary CTA
     * and optional secondary button. Determines button visibility, labels, and link behavior.
     */
    buttons: HeroBlockType['buttons'];
    /**
     * The configuration for the hero section's headline, including text content, layout width,
     * and whether the headline should span the full width.
     */
    headline: HeroBlockType['headline'];
    /**
     * An optional `id` attribute for the headline element, allowing for direct linking or accessibility targeting.
     */
    id?: string | null;
    /**
     * If `true`, the component is rendered as purely presentational, applying `aria-hidden` and
     * `role="presentation"` for accessibility. Useful for decorative or non-interactive variants.
     *
     * @default false
     */
    isPresentational?: boolean;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'HeroContent'
     */
    testId?: string;
    /**
     * The visual theme of the hero section, determining text and button colors.
     * `'dark'` applies a darker style, while `'light'` applies a lighter style.
     */
    theme: 'dark' | 'light';
}

/**
 * A presentation component that renders the main hero section headline and optional CTA buttons.
 * This component consumes preprocessed hero content data from the `useHeroContent` hook to display
 * a styled headline with an optional maximum width restriction, followed by one or two CTA buttons.
 * It supports both semantic and purely presentational rendering modes through the `isPresentational`
 * flag, which adds `aria-hidden` and `role="presentation"` attributes when enabled.
 * The component is wrapped in MobX's `observer` to ensure it re-renders automatically when observable
 * state changes. It applies appropriate `rel`, `target`, and `tabIndex` attributes for accessibility
 * and interaction control, including disabling focus for presentational content.
 *
 * @param props                  The component props.
 * @param props.buttons          The hero section button configuration, including `ctaButton` and `secondaryButton`.
 * @param props.headline         The headline configuration, containing text, width, and layout flags.
 * @param props.id               Optional `id` attribute for the headline element.
 * @param props.isPresentational If `true`, the component is rendered as purely presentational with no accessibility semantics.
 * @param props.testId           Optional test identifier applied as `data-testid` to the headline wrapper. Defaults to `'HeroContent'`.
 * @param props.theme            The theme (`'dark'` or `'light'`) used to determine text and button colors.
 * @returns A JSX fragment containing the hero headline and optional CTA buttons.
 *
 * @example
 * ```tsx
 * <HeroContent
 *   theme="dark"
 *   headline={{ text: 'Welcome!', headlineFullWidth: false, headlineWidth: 'lg' }}
 *   buttons={{
 *     headerButtons: 'bothButtons',
 *     ctaButton: { label: 'Get Started', linkType: 'internal', internal: '/start' },
 *     secondaryButton: { label: 'Learn More', linkType: 'external', url: 'https://example.com' }
 *   }}
 * />
 * ```
 */
const HeroContent = observer(({
    buttons,
    headline,
    id,
    isPresentational = false,
    testId = 'HeroContent',
    theme
}: ComponentProps) => {
    const {
        ariaProps,
        ctaHref,
        ctaRel,
        ctaTarget,
        ctaText,
        hasButtonOne,
        hasButtonTwo,
        headlineColor,
        secondHref,
        secondRel,
        secondTarget,
        secondText
    } = useHeroContent(theme, buttons, isPresentational);

    return (
        <>
            <Wrapper
                $hasMaxWidth={headline.headlineFullWidth}
                $maxWith={headline.headlineWidth}
                data-testid={testId}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...ariaProps}
            >
                <H1 id={id ?? undefined}><Color $color={headlineColor}>{headline.text}</Color></H1>
            </Wrapper>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <Spacer y={10} isNotStretching {...ariaProps} />
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <ButtonWrapper {...ariaProps}>
                {hasButtonOne && (
                    <Button
                        as="link"
                        href={ctaHref}
                        rel={ctaRel}
                        // eslint-disable-next-line @nfq/no-magic-numbers
                        tabIndex={isPresentational ? -1 : undefined}
                        target={ctaTarget}
                        theme={theme}
                        variant="secondary"
                    >
                        {ctaText}
                    </Button>
                )}
                {hasButtonTwo && (
                    <Button
                        as="link"
                        href={secondHref}
                        rel={secondRel}
                        // eslint-disable-next-line @nfq/no-magic-numbers
                        tabIndex={isPresentational ? -1 : undefined}
                        target={secondTarget}
                        theme={theme}
                        variant="outlined"
                    >
                        {secondText}
                    </Button>
                )}
            </ButtonWrapper>
        </>
    );
});

HeroContent.displayName = 'HeroContent';

export {HeroContent};

interface WrapperProps {
    $hasMaxWidth?: boolean | null | undefined;
    $maxWith?: number | null | undefined;
}

const Wrapper = styled.div<WrapperProps>`
    max-width: ${({$hasMaxWidth, $maxWith}) => ($hasMaxWidth ? `${$maxWith}px` : '100%')};
    width: 100%;
`;

const ButtonWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(5)};
`;